import AppDescription from '@nxsys/components/AppDescription';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch } from '@nxsys/store';
import { prospectActions } from '@nxsys/store/slices/prospect/reducer/prospectSlice';
import { AgencyProspect } from '@nxsys/store/slices/prospect/types';
import { Icons } from '@nxsys/types/icons';
import { Descriptions } from 'antd';
import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import AgencyForm, { AgencyFormRef, AgencyFormValues } from './AgencyForm';

type CreateAgencyProps = {
  isOpen: boolean;
  toggle: () => void;
  data: AgencyProspect | null;
  refetchData: () => void;
  handleProgressRegistrationByIds: (ids: string[]) => void;
};

const ViewAgencyModal: React.FC<CreateAgencyProps> = ({
  isOpen,
  toggle,
  data,
  refetchData,
  handleProgressRegistrationByIds,
}: CreateAgencyProps) => {
  const { messages } = useTypedIntl();

  const dispatch = useDispatch<AppDispatch>();

  const formRef = useRef<AgencyFormRef>(null);

  const [isEditing, setIsEditing] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values: AgencyFormValues) => {
    if (!data?.id) return;

    setIsSubmitting(true);
    try {
      const params = {
        email: values.email ?? null,
        phone: values.phone ?? null,
        name: values.name ?? null,
        contactName: values.contactName ?? null,
      };
      // unwrap() so a rejected request throws — without it the thunk resolves
      // and the modal closes as if the update had succeeded
      await dispatch(
        prospectActions.updateAgencyProspectById({
          id: data.id,
          payload: params,
        }),
      ).unwrap();
      refetchData();
      setIsEditing(false);
      toggle();
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const descriptionItems = [
    {
      label: 'Agency Name',
      value: data?.name ?? '',
    },
    {
      label: 'Contact Name',
      value: data?.contactName ?? '',
    },
    {
      label: 'Email',
      value: data?.email ?? '',
    },
    {
      label: 'Phone Number',
      value: data?.phone ?? '',
    },
  ];

  return (
    <AppModalContainer
      title={messages['prospect.agencyDetail']}
      isOpen={isOpen}
      toggle={toggle}
      // Unmount on close so unsaved edits are dropped and the next agency
      // opens with its own values
      destroyOnClose
      actions={
        isEditing
          ? [
              {
                name: messages['common.cancel'],
                variant: 'outlined',
                onClick: () => setIsEditing(false),
              },
              {
                name: messages['common.save'],
                buttonType: 'primary',
                onClick: () => formRef.current?.submit(),
                disabled: isSubmitting,
                loading: isSubmitting,
              },
            ]
          : [
              {
                name: messages['prospect.candidate.progressToRegistration'],
                variant: 'outlined',
                leftIcon: (
                  <Icons.IconLogin
                    color={defaultTheme.theme.palette.primary.main}
                  />
                ),
                onClick: () => {
                  if (data?.id) {
                    handleProgressRegistrationByIds([data.id]);
                    toggle();
                  }
                },
              },
              {
                name: messages['common.edit'],
                buttonType: 'primary',
                leftIcon: (
                  <Icons.IconEdit color={defaultTheme.theme.palette.white} />
                ),
                onClick: () => setIsEditing(true),
              },
            ]
      }
    >
      {!isEditing ? (
        <AppDescription
          column={1}
          labelStyle={{ width: 216 }}
          bordered={false}
          colon={false}
          size="middle"
        >
          {descriptionItems?.map((item, _index) => (
            <Descriptions.Item key={_index} label={item.label}>
              {item.value ?? '-'}
            </Descriptions.Item>
          ))}
        </AppDescription>
      ) : (
        <AgencyForm
          formRef={formRef}
          onSubmit={handleSubmit}
          isEditing
          defaultValues={{
            name: data?.name ?? '',
            email: data?.email ?? '',
            phone: data?.phone ?? '',
            contactName: data?.contactName ?? '',
          }}
        />
      )}
    </AppModalContainer>
  );
};

export default ViewAgencyModal;
